import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as THREE from 'three';
import '../styles/Hero.css';

gsap.registerPlugin(TextPlugin, ScrollTrigger);

const cves = [
  { id: 'CVE-2025-55182', product: 'Apache HTTP Server', severity: 'CRITICAL', score: 9.8 },
  { id: 'CVE-2025-64446', product: 'OpenSSL', severity: 'HIGH', score: 7.5 },
  { id: 'CVE-2025-55177', product: 'Linux Kernel', severity: 'MEDIUM', score: 6.1 },
  { id: 'CVE-2025-49113', product: 'nginx', severity: 'HIGH', score: 8.2 },
  { id: 'CVE-2025-31200', product: 'Microsoft Exchange', severity: 'CRITICAL', score: 9.1 },
  { id: 'CVE-2025-27732', product: 'Windows LDAP', severity: 'HIGH', score: 8.8 },
  { id: 'CVE-2025-26633', product: 'Node.js', severity: 'MEDIUM', score: 5.9 },
  { id: 'CVE-2025-21194', product: 'VMware ESXi', severity: 'CRITICAL', score: 9.3 },
  { id: 'CVE-2025-47900', product: 'Kubernetes', severity: 'HIGH', score: 7.9 },
  { id: 'CVE-2025-33445', product: 'PostgreSQL', severity: 'LOW', score: 3.1 },
];

const stats = [
  { end: 240, suffix: 'k+', label: 'CVEs Tracked' },
  { end: 2, suffix: 'h avg', label: 'Detection Lag', prefix: '<' },
  { end: 99.9, suffix: '%', label: 'Uptime SLA', decimal: true },
  { end: 50, suffix: 'k+', label: 'Threats Mapped' },
];

const severityColor = (s) => ({ CRITICAL: '#E24B4A', HIGH: '#EF9F27', MEDIUM: '#BA7517', LOW: '#1D9E75' }[s] || '#8ca0c8');

function ParticleNetwork({ containerRef }) {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, canvas.clientWidth / canvas.clientHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const particleCount = 120;
    const positions = new Float32Array(particleCount * 3);
    const velocities = [];

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
      velocities.push({
        x: (Math.random() - 0.5) * 0.008,
        y: (Math.random() - 0.5) * 0.008,
        z: (Math.random() - 0.5) * 0.004,
      });
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0x3b82f6,
      size: 0.06,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    const lineGeometry = new THREE.BufferGeometry();
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.08,
      blending: THREE.AdditiveBlending,
    });
    const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
    scene.add(lines);

    camera.position.z = 8;

    const handleMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouseRef.current.y = -((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };

    const container = containerRef?.current || canvas.parentElement;
    container.addEventListener('mousemove', handleMouse, { passive: true });

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);

      const pos = geometry.attributes.position.array;
      for (let i = 0; i < particleCount; i++) {
        pos[i * 3] += velocities[i].x;
        pos[i * 3 + 1] += velocities[i].y;
        pos[i * 3 + 2] += velocities[i].z;

        if (Math.abs(pos[i * 3]) > 10) velocities[i].x *= -1;
        if (Math.abs(pos[i * 3 + 1]) > 6) velocities[i].y *= -1;
        if (Math.abs(pos[i * 3 + 2]) > 4) velocities[i].z *= -1;
      }
      geometry.attributes.position.needsUpdate = true;

      const linePositions = [];
      for (let i = 0; i < particleCount; i++) {
        for (let j = i + 1; j < particleCount; j++) {
          const dx = pos[i * 3] - pos[j * 3];
          const dy = pos[i * 3 + 1] - pos[j * 3 + 1];
          const dz = pos[i * 3 + 2] - pos[j * 3 + 2];
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist < 2.5) {
            linePositions.push(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);
            linePositions.push(pos[j * 3], pos[j * 3 + 1], pos[j * 3 + 2]);
          }
        }
      }

      lineGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(linePositions, 3)
      );

      camera.position.x += (mouseRef.current.x * 0.5 - camera.position.x) * 0.02;
      camera.position.y += (mouseRef.current.y * 0.3 - camera.position.y) * 0.02;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      container.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      lineGeometry.dispose();
      lineMaterial.dispose();
    };
  }, [containerRef]);

  return <canvas ref={canvasRef} className="hero__particles" />;
}

function StatCard({ end, suffix, label, prefix = '', decimal = false, run }) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!run) return;
    const dur = 1800;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(e * end);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [run, end]);

  const display = decimal ? val.toFixed(1) : Math.floor(val);

  return (
    <div className="stat-card">
      <div className="stat-card__num font-mono">{prefix}{display}{suffix}</div>
      <div className="stat-card__label">{label}</div>
    </div>
  );
}

const TechOrbits = () => {
  return (
    <div className="orbit-system">
      <div className="orbit-center">
        <img src={process.env.PUBLIC_URL + '/CVEARITY1.png'} alt="CVEarity Logo" />
      </div>
      
      <div className="orbit orbit-inner">
        <div className="orbit-icon" style={{ top: '0%', left: '50%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" alt="React" />
        </div>
        <div className="orbit-icon" style={{ top: '75%', left: '93.3%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg" alt="Node.js" />
        </div>
        <div className="orbit-icon" style={{ top: '75%', left: '6.7%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" alt="Vue" />
        </div>
      </div>

      <div className="orbit orbit-middle">
        <div className="orbit-icon" style={{ top: '14.6%', left: '85.4%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" alt="Docker" />
        </div>
        <div className="orbit-icon" style={{ top: '85.4%', left: '85.4%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nginx/nginx-original.svg" alt="NGINX" />
        </div>
        <div className="orbit-icon" style={{ top: '85.4%', left: '14.6%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg" alt="MongoDB" />
        </div>
        <div className="orbit-icon" style={{ top: '14.6%', left: '14.6%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg" alt="Python" />
        </div>
      </div>

      <div className="orbit orbit-outer">
        <div className="orbit-icon" style={{ top: '0%', left: '50%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg" alt="Kubernetes" />
        </div>
        <div className="orbit-icon" style={{ top: '34.6%', left: '97.6%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angular/angular-original.svg" alt="Angular" />
        </div>
        <div className="orbit-icon" style={{ top: '90.5%', left: '79.4%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/wordpress/wordpress-plain.svg" alt="WordPress" />
        </div>
        <div className="orbit-icon" style={{ top: '90.5%', left: '20.6%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" alt="Redis" />
        </div>
        <div className="orbit-icon" style={{ top: '34.6%', left: '2.4%' }}>
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg" alt="Linux" />
        </div>
      </div>
    </div>
  );
};

export default function Hero() {
  const heroRef = useRef(null);
  const headlineEl = useRef(null);
  const cursorEl = useRef(null);
  const statsEl = useRef(null);
  const dashRef = useRef(null);
  const [statsRun, setStatsRun] = useState(false);

  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.4 });
    tl.to(headlineEl.current, {
      duration: 2.2,
      text: { value: 'Delivering Clarity in the Chaos of Cybersecurity Threats', delimiter: '' },
      ease: 'none',
    }).to(cursorEl.current, {
      opacity: 0, duration: 0.15, repeat: 5, yoyo: true,
    }).to(cursorEl.current, { opacity: 0 });
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setStatsRun(true); },
      { threshold: 0.3 }
    );
    if (statsEl.current) obs.observe(statsEl.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to('.hero__left', {
        y: -40,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero',
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });

      gsap.to('.hero__right', {
        y: -20,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero',
          start: 'top top',
          end: 'bottom top',
          scrub: 1.5,
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);



  return (
    <section id="home" className="hero" ref={heroRef}>

      <div className="container hero__grid">
        <div className="hero__left">
          <motion.div
            className="hero__badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15 }}
          >
            <span className="hero__badge-dot" />
            Vulnerability Intelligence Platform
            <span className="hero__badge-live">● LIVE</span>
          </motion.div>

          <motion.h1
            className="hero__h1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.35 }}
          >
            <span ref={headlineEl} />
            <span ref={cursorEl} className="cursor-blink" />
          </motion.h1>

          <motion.p
            className="hero__sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.85 }}
          >
            Cut through thousands of vulnerabilities with automated matching,
            smart prioritization, and real-time alerts. CVEarity empowers you
            to secure your applications with clarity and confidence.
          </motion.p>

          <motion.div
            className="hero__actions"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 1.05 }}
          >
            <a href="https://cvearity.com/book-a-demo.html" target="_blank" rel="noopener noreferrer" className="btn-primary hero__btn">
              Book a Demo
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                <path d="M3 7.5H12M12 7.5L8 3.5M12 7.5L8 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </a>
            <button className="btn-secondary" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>
              Contact Us
            </button>
          </motion.div>

          <motion.div
            className="hero__stats"
            ref={statsEl}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.25 }}
          >
            {stats.map((s, i) => <StatCard key={i} {...s} run={statsRun} />)}
          </motion.div>
        </div>

        <motion.div
          className="hero__right"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.85, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <TechOrbits />
        </motion.div>
      </div>

      <div className="ticker">
        <div className="ticker__label">
          <span className="ticker__dot" />
          LIVE
        </div>
        <div className="ticker__mask">
          <div className="ticker__track">
            {[...cves, ...cves].map((c, i) => (
              <span key={i} className="ticker__item">
                <span className="ticker__id font-mono" style={{ color: severityColor(c.severity) }}>{c.id}</span>
                <span className="ticker__mid">·</span>
                <span className="ticker__prod">{c.product}</span>
                <span className="ticker__sev font-mono" style={{ color: severityColor(c.severity), background: `${severityColor(c.severity)}18` }}>
                  {c.severity} {c.score}
                </span>
                <span className="ticker__sep">|</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
